import { useAuth } from "context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { AppBar, Toolbar, Typography, Box, Avatar, Tabs, Tab, Button, IconButton, Badge, Stack, Grow, useMediaQuery, useTheme } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Logo from "components/Logo";
import "bootstrap/dist/css/bootstrap.min.css";
import { Row, Col, Table } from "react-bootstrap";
import { useEffect, useState, useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TextField } from "@mui/material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Notifications from "components/Notifications/index";
import { Snackbar, Alert } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeIcon from "@mui/icons-material/Home";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CommentIcon from "@mui/icons-material/Comment";
import SendIcon from "@mui/icons-material/Send";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import QBPayment from "layouts/qb-payment";
import Upsells from "layouts/upsells";

const rawHostawayToken = process.env.REACT_APP_HOSTAWAY_API_TOKEN || process.env.HOSTAWAY_AUTH_TOKEN || "";
const API_TOKEN = rawHostawayToken.startsWith("Bearer ") ? rawHostawayToken.substring(7).trim() : rawHostawayToken.trim();

const TEABLE_TOKEN = process.env.REACT_APP_TEABLE_API_TOKEN_FDO;

const NOTIFICATION_API = `${process.env.REACT_APP_TEABLE_BASE_URL || 'https://teable.namuve.com'}/api/table/tblP2sVyihyhLjXYtRI/record?filter=${encodeURIComponent(
  JSON.stringify({
    conjunction: "or",
    filterSet: [
      {
        fieldId: "Time",
        operator: "is",
        value: { mode: "today", timeZone: "Asia/Karachi" },
      },
      {
        fieldId: "Time",
        operator: "is",
        value: { mode: "yesterday", timeZone: "Asia/Karachi" },
      },
    ],
  })
)}&sort=${encodeURIComponent(
  JSON.stringify({ fieldId: "Time", direction: "desc" })
)}`;

function UserLayout({ children }) {
  const { logout, user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0); // Default = Home

  // Synchronize activeTab with URL path
  useEffect(() => {
    const path = location.pathname;
    if (path === "/hostaway-reservation") {
      setActiveTab(3);
    } else if (path === "/upsell-list") {
      setActiveTab(4);
    } else if (path === "/charges-list") {
      setActiveTab(5);
    } else if (path === "/upsells") {
      setActiveTab(3);
    } else if (path === "/qb-payments") {
      setActiveTab(6);
    } else if (path === "/fdo-panel" || path === "/") {
      if (activeTab >= 3) {
        setActiveTab(0);
      }
    }
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate("/fdo-panel");
    else if (newValue === 3) navigate("/hostaway-reservation");
    else if (newValue === 4) navigate("/upsell-list");
    else if (newValue === 5) navigate("/charges-list");
    else if (newValue === 6) navigate("/qb-payments");
  };
  const [listingSections, setListingSections] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [todayCheckIn, setTodayCheckIn] = useState([]);
  const [todayCheckOut, setTodayCheckOut] = useState([]);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [errorCheck, setErrorCheck] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});

  const handleCommentChange = (id, value) => {
    setCommentInputs((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSendComment = async (row) => {
    const comment = commentInputs[row.id];
    if (!comment || !comment.trim()) {
      alert("Please enter a comment.");
      return;
    }

    try {
      const res = await fetch("/api/fdo/comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reservation_id: row.id,
          comment: comment,
          user: user?.name || user?.username || "Unknown FDO",
        }),
      });

      if (res.ok) {
        // Success
      } else {
        const errData = await res.json();
        alert("Failed to send comment: " + (errData.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error sending comment:", error);
      alert("Network error while sending comment.");
    }
  };

  const fetchExistingComments = async (reservationIds) => {
    if (!reservationIds || reservationIds.length === 0) return;

    try {
      const res = await fetch(`/api/fdo/comments`);
      if (!res.ok) return;

      const data = await res.json();
      const records = data.records || [];
      const commentsMap = {};

      records.forEach((record) => {
        const resId = record.reservation_id;
        const comment = record.comment;
        if (resId && comment) {
          if (!commentsMap[resId] || record.createdAt > commentsMap[resId].time) {
            commentsMap[resId] = { comment, time: record.createdAt };
          }
        }
      });

      setCommentInputs((prev) => {
        const updated = { ...prev };
        Object.keys(commentsMap).forEach((resId) => {
          updated[resId] = commentsMap[resId].comment;
        });
        return updated;
      });
    } catch (error) {
      console.error("Error fetching existing comments:", error);
    }
  };
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const anchorRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [prevCount, setPrevCount] = useState(0);
  const [latestNotification, setLatestNotification] = useState(null);
  const notifiedRecordIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true); // Track initial load
  // 🔔 Notification Stack State
  const [snackPack, setSnackPack] = useState([]);

  // Add function
  const playNotificationSound = () => {
    const audio = new Audio("/notification.mp3"); // Put in public/
    audio.volume = 0.4;
    audio.play().catch(() => { });
  };

  // useEffect
  useEffect(() => {
    const fetchNotificationUpdate = async () => {
      try {
        const res = await fetch("/api/fdo/notifications");
        if (!res.ok) return;

        const data = await res.json();
        const records = data.records || [];

        // === Get only NEW records (not seen before) ===
        const newRecords = records.filter(
          (record) => !notifiedRecordIdsRef.current.has(record.id)
        );

        if (newRecords.length === 0) {
          return; // ← Keep badge unchanged
        }

        // === Badge:INCREMENT by number of new records (ALWAYS, even on initial load) ===
        setUnreadCount((prev) => prev + newRecords.length);

        // 🔄 On initial load, just mark all as seen without showing snackbars
        if (isInitialLoadRef.current) {
          newRecords.forEach((record) => {
            notifiedRecordIdsRef.current.add(record.id);
          });
          isInitialLoadRef.current = false;
          console.log("🔕 Initial load: marked", newRecords.length, "notifications as seen (no snackbars)");
          return; // Don't show snackbars on first load
        }

        const currentUserNames = [
          user?.name,
          user?.username,
          user?.email?.split("@")[0],
        ]
          .filter(Boolean)
          .map((s) => s.trim().toLowerCase());

        const newOtherComments = [];

        for (const record of newRecords) {
          const sender = record?.user?.trim() || "";
          const senderLower = sender.toLowerCase();
          const cleanSender = senderLower
            .replace(/\s*\(you\)$/i, "")
            .replace(/\s*-\s*fdo$/i, "")
            .replace(/\s*admin$/i, "")
            .trim();

          const isOwnComment = currentUserNames.some((name) => {
            const cleanName = name.replace(/[@.]/g, " ");
            // Use strict equality to avoid false positives (e.g. "User" matching "User 2")
            return (
              cleanSender === name ||
              cleanSender === cleanName
            );
          });

          notifiedRecordIdsRef.current.add(record.id);

          if (!isOwnComment) {
            newOtherComments.push({
              sender,
              record: record,
            });
          }
        }

        // Show ALL new comments — stacked
        if (newOtherComments.length > 0) {
          newOtherComments.forEach((comment, index) => {
            const newId = new Date().getTime() + Math.random();

            // Add to stack with a slight delay for visual separation if multiple arrive at once
            setTimeout(() => {
              setSnackPack((prev) => [
                ...prev,
                {
                  id: newId,
                  message: "New Comment",
                  severity: "info",
                  reservationId: comment.record.reservation_id, // ✅ Add ID for click handler
                  richContent: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CommentIcon sx={{ fontSize: 18 }} />
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          lineHeight: 1.4,
                          color: "#fff",
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{comment.sender}</span>
                        <span style={{ color: "#dbeafe" }}>commented on</span>
                        <span style={{ color: "#fbbf24", fontWeight: 600 }}>
                          {comment.record.guest_name || "Guest"}
                        </span>
                        <span style={{ color: "#34d399", fontWeight: 600 }}>
                          {comment.record.apt || "Unit"}
                        </span>
                      </Typography>
                    </Box>
                  ),
                },
              ]);
              playNotificationSound();

              // Auto-dismiss after 5 seconds
              setTimeout(() => {
                setSnackPack((prev) => prev.filter((item) => item.id !== newId));
              }, 5000);
            }, index * 300); // 300ms stagger
          });
        }

      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    fetchNotificationUpdate();
    const interval = setInterval(fetchNotificationUpdate, 30000);
    return () => clearInterval(interval);
  }, [user?.name, user?.username, user?.email]); // ← include email

  async function downloadExcel() {
    if (loadingCheck) {
      alert("Please wait... Data is still loading.");
      return;
    }
    if (todayCheckIn.length === 0 && todayCheckOut.length === 0) {
      alert("No data available to download.");
      return;
    }

    const showPhone = !(user?.role === "user" || (user?.role === "custom" && hasPermission("fdoPanel", "hideContact")));
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-GB");
    const fileDate = dateStr.replace(/\//g, "-");

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Upcoming Reservations");

    // ===== ROW 1 =====
    if (showPhone) {
      ws.mergeCells("B1:O1");
    } else {
      ws.mergeCells("B1:N1");
    }
    ws.getCell("A1").value = dateStr;
    ws.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell("A1").font = { bold: true };

    ws.getCell("B1").value = "Upcoming Reservation";
    ws.getCell("B1").alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell("B1").font = { bold: true, size: 13 };

    // ===== ROW 2 =====
    if (showPhone) {
      ws.mergeCells("A2:H2");
      ws.getCell("A2").value = "Today Upcoming Arrivals";
      ws.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("A2").font = { bold: true, size: 12 };

      ws.mergeCells("I2:O2");
      ws.getCell("I2").value = "Today Upcoming Departures";
      ws.getCell("I2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("I2").font = { bold: true, size: 12 };
    } else {
      ws.mergeCells("A2:G2");
      ws.getCell("A2").value = "Today Upcoming Arrivals";
      ws.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("A2").font = { bold: true, size: 12 };

      ws.mergeCells("H2:N2");
      ws.getCell("H2").value = "Today Upcoming Departures";
      ws.getCell("H2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("H2").font = { bold: true, size: 12 };
    }

    // ===== ROW 3 HEADERS =====
    const headers = showPhone
      ? [
          "Sr#",
          "Apt#",
          "Name",
          "Phone#",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
          "Sr#",
          "Apt#",
          "Name",
          "Phone#",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
        ]
      : [
          "Sr#",
          "Apt#",
          "Name",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
          "Sr#",
          "Apt#",
          "Name",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
        ];
    ws.addRow(headers);

    ws.getRow(3).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF17621B" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // ===== DATA ROWS =====
    const maxLength = Math.max(todayCheckIn.length, todayCheckOut.length);
    for (let i = 0; i < maxLength; i++) {
      const checkIn = todayCheckIn[i] || {};
      const checkOut = todayCheckOut[i] || {};

      const rowData = [
        checkIn.apartment ? i + 1 : "",
        checkIn.apartment || "",
        checkIn.guest || "",
        ...(showPhone ? [checkIn.phone || ""] : []),
        checkIn.vehicle || "",
        "", // Reminder
        commentInputs[checkIn.id] || "", // Status (from comments)
        checkIn.apartment
          ? checkIn.paymentStatus &&
            checkIn.paymentStatus.toLowerCase() !== "unknown"
            ? checkIn.paymentStatus
            : "Due"
          : "",

        checkOut.apartment ? i + 1 : "",
        checkOut.apartment || "",
        checkOut.guest || "",
        ...(showPhone ? [checkOut.phone || ""] : []),
        checkOut.vehicle || "",
        "", // Reminder
        commentInputs[checkOut.id] || "", // Status (from comments)
        checkOut.apartment
          ? checkOut.paymentStatus &&
            checkOut.paymentStatus.toLowerCase() !== "unknown"
            ? checkOut.paymentStatus
            : "Due"
          : "",
      ];

      ws.addRow(rowData);
    }

    // ===== STYLING =====
    for (let r = 4; r <= ws.rowCount; r++) {
      const row = ws.getRow(r);
      const fillColor = r % 2 === 0 ? "FFF5F7F5" : "FFFFFFFF";
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    }

    // ===== AUTO WIDTH FOR NORMAL COLUMNS =====
    ws.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (!cell.value) return;
        const len = cell.value.toString().trim().length;
        if (len > maxLength) maxLength = len;
      });
      column.width = Math.max(maxLength * 0.9, 4);
    });

    // ===== SHRINK SMALL COLUMNS (Sr#, Apt#, Vehicle#, Payment) =====
    const shrinkCols = showPhone
      ? ["A", "B", "E", "H", "I", "J", "M", "N"]
      : ["A", "B", "D", "G", "H", "I", "K", "N"];
    shrinkCols.forEach((col) => {
      let maxLen = 0;
      ws.getColumn(col).eachCell({ includeEmpty: true }, (cell) => {
        if (!cell.value) return;
        const len = cell.value.toString().trim().length;
        if (len > maxLen) maxLen = len;
      });
      ws.getColumn(col).width = Math.min(maxLen + 1, 9);
    });

    // ===== FINAL ALIGNMENT + SHRINK FIT =====
    ws.eachRow({ includeEmpty: true }, (row) => {
      row.eachCell((cell) => {
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: false,
          shrinkToFit: true,
        };
      });
    });

    ws.getRow(1).height = 25;
    ws.getRow(2).height = 20;
    ws.getRow(3).height = 23;

    // ===== SAVE FILE =====
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Upcoming_Reservation_${fileDate}.xlsx`);
  }

  function downloadPDF() {
    if (loadingCheck) {
      alert("Please wait... Data is still loading.");
      return;
    }
    if (todayCheckIn.length === 0 && todayCheckOut.length === 0) {
      alert("No data available to download.");
      return;
    }

    const showPhone = !(user?.role === "user" || (user?.role === "custom" && hasPermission("fdoPanel", "hideContact")));
    const doc = new jsPDF({ orientation: "landscape", unit: "mm" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const cellHeight = 8;
    let y = 10;

    // === COLUMN WIDTHS ===
    const colWidths = showPhone
      ? [
          10, 18, 35, 28, 25, 28, 20, 20, // Left table (8)
          10, 18, 35, 28, 25, 20, 20      // Right table (7)
        ]
      : [
          10, 18, 35, 25, 28, 20, 20,     // Left table (7)
          10, 18, 35, 25, 20, 20          // Right table (6)
        ];
    const totalColWidth = colWidths.reduce((a, b) => a + b, 0);
    const availableWidth = pageWidth - 2 * margin;
    const gap = 2;
    const scale = (availableWidth - gap) / totalColWidth;
    const scaledWidths = colWidths.map(w => w * scale);

    const leftCount = showPhone ? 8 : 7;
    const rightStartIndex = showPhone ? 8 : 7;

    // === Helper: Auto-fit text ===
    const fitText = (text, maxWidth, baseSize = 8.5) => {
      let size = baseSize;
      doc.setFontSize(size);
      while (doc.getTextWidth(text) > maxWidth && size > 5) {
        size -= 0.3;
        doc.setFontSize(size);
      }
      return size;
    };

    // === Helper: New Page Header (for pagination) ===
    const drawHeader = () => {
      const dateStr = new Date().toLocaleDateString("en-GB");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(dateStr, margin + 2, y + 5.5);

      const titleStartX = margin + scaledWidths[0];
      const titleWidth = scaledWidths.slice(1).reduce((a, b) => a + b, 0) + gap;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Upcoming Reservation", titleStartX + titleWidth / 2, y + 5.5, { align: "center" });

      y += cellHeight + 2;

      // === SECTION TITLES ===
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);

      const leftStartX = margin;
      const leftWidth = scaledWidths.slice(0, leftCount).reduce((a, b) => a + b, 0);
      const rightStartX = leftStartX + leftWidth + gap;
      const rightWidth = scaledWidths.slice(leftCount).reduce((a, b) => a + b, 0);

      // LEFT
      doc.setFillColor(220, 220, 220);
      doc.rect(leftStartX, y, leftWidth, cellHeight, "F");
      doc.text("Today Upcoming Arrivals", leftStartX + leftWidth / 2, y + 5.5, { align: "center" });

      // RIGHT
      doc.setFillColor(220, 220, 220);
      doc.rect(rightStartX, y, rightWidth, cellHeight, "F");
      doc.text("Today Upcoming Departures", rightStartX + rightWidth / 2, y + 5.5, { align: "center" });

      // Divider lines
      doc.setDrawColor(200, 200, 200);
      doc.line(rightStartX, y, rightStartX, y + cellHeight);

      y += cellHeight + 1;

      // === HEADERS ===
      const headers = showPhone
        ? [
            "Sr#", "Apt#", "Name", "Phone#", "Vehicle#", "Reservation Status", "Remarks", "Payment",
            "Sr#", "Apt#", "Name", "Phone#", "Vehicle#", "Remarks", "Payment"
          ]
        : [
            "Sr#", "Apt#", "Name", "Vehicle#", "Reservation Status", "Remarks", "Payment",
            "Sr#", "Apt#", "Name", "Vehicle#", "Remarks", "Payment"
          ];

      doc.setFont("helvetica", "bold");
      doc.setTextColor(255);

      let x = margin;
      headers.forEach((header, i) => {
        if (i === rightStartIndex) x += gap;
        const width = scaledWidths[i];
        doc.setFillColor(23, 98, 27);
        doc.rect(x, y, width, cellHeight, "F");

        const maxWidth = width - 3;
        const fittedSize = fitText(header, maxWidth, 8.5);
        doc.setFontSize(fittedSize);
        doc.text(header, x + width / 2, y + 5.3, { align: "center" });

        x += width;
      });

      y += cellHeight;

      // ✅ Reset text color and font for data rows
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
    };

    // === Draw header for the first page ===
    drawHeader();

    // === DATA ROWS ===
    const checkInData = todayCheckIn;
    const checkOutData = todayCheckOut;
    const rowHeight = 7;
    const maxLength = Math.max(checkInData.length, checkOutData.length);

    doc.setTextColor(0);
    doc.setDrawColor(200, 200, 200);
    doc.setFont("helvetica", "normal");

    for (let i = 0; i < maxLength; i++) {
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");

      // Page break if needed
      if (y + rowHeight > pageHeight - 10) {
        doc.addPage();
        y = 10;

        // 🔧 Reset colors to prevent white text on new pages
        doc.setTextColor(0, 0, 0);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(200, 200, 200);

        drawHeader();
      }

      const isEven = i % 2 === 0;
      const bg = isEven ? [245, 247, 245] : [255, 255, 255];
      const checkIn = checkInData[i] || {};
      const checkOut = checkOutData[i] || {};

      // LEFT
      let x = margin;
      const leftData = [
        checkInData[i] ? (i + 1).toString() : "",
        checkIn.apartment || "",
        checkIn.guest || "",
        ...(showPhone ? [checkIn.phone || ""] : []),
        checkIn.vehicle || "",
        "",
        "",
        checkInData[i]
          ? (checkIn.paymentStatus &&
            checkIn.paymentStatus.toLowerCase() !== "unknown"
            ? checkIn.paymentStatus
            : "Due")
          : ""
      ];

      leftData.forEach((cell, j) => {
        const width = scaledWidths[j];
        doc.setFillColor(...bg);
        doc.rect(x, y, width, rowHeight, "F");
        doc.rect(x, y, width, rowHeight, "S");
        if (cell) {
          const maxWidth = width - 4;
          const fittedSize = fitText(cell, maxWidth, 8.2);
          doc.setFontSize(fittedSize);
          doc.text(cell, x + width / 2, y + 4.5, { align: "center" });
        }
        x += width;
      });

      // RIGHT
      x = margin + scaledWidths.slice(0, leftCount).reduce((a, b) => a + b, 0) + gap;
      const rightData = [
        checkOutData[i] ? (i + 1).toString() : "",
        checkOut.apartment || "",
        checkOut.guest || "",
        ...(showPhone ? [checkOut.phone || ""] : []),
        checkOut.vehicle || "",
        "",
        checkOutData[i]
          ? (checkOut.paymentStatus &&
            checkOut.paymentStatus.toLowerCase() !== "unknown"
            ? checkOut.paymentStatus
            : "Due")
          : ""
      ];

      rightData.forEach((cell, j) => {
        const width = scaledWidths[j + leftCount];
        doc.setFillColor(...bg);
        doc.rect(x, y, width, rowHeight, "F");
        doc.rect(x, y, width, rowHeight, "S");
        if (cell) {
          const maxWidth = width - 4;
          const fittedSize = fitText(cell, maxWidth, 8.2);
          doc.setFontSize(fittedSize);
          doc.text(cell, x + width / 2, y + 4.5, { align: "center" });
        }
        x += width;
      });

      y += rowHeight;
    }

    const dateStr = new Date().toLocaleDateString("en-GB");
    doc.save(`Upcoming_Reservation_${dateStr.replace(/\//g, "-")}.pdf`);
  }

  useEffect(() => {
    if (activeTab !== 0) return;

    // 🕒 Prevent multiple rapid triggers (10s cooldown)
    if (window.__homeCooldown && Date.now() - window.__homeCooldown < 5000) {
      console.log("⏳ Skipping Home refresh — still in cooldown");
      return;
    }

    // Mark cooldown start
    window.__homeCooldown = Date.now();

    // 🟡 Show loading immediately
    setLoading(true);

    const timer = setTimeout(() => {
      console.log("🏠 Rendering Home tab after 10s delay");
      setLoading(false);
    }, 5000);

    return () => {
      clearTimeout(timer);
      window.__homeCooldown = null; // reset cooldown if user left early
    };

  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 2) return;

    // 🕒 Prevent multiple rapid triggers
    if (window.__checkInOutCooldown && Date.now() - window.__checkInOutCooldown < 5000) {
      console.log("⏳ Skipping fetch — still in cooldown");
      return;
    }

    // Mark cooldown start
    window.__checkInOutCooldown = Date.now();

    // 🟡 Immediately show loading state
    setLoadingCheck(true);
    setErrorCheck(null);
    setTodayCheckIn([]);
    setTodayCheckOut([]);

    // Wait 10 seconds before fetching
    const timer = setTimeout(() => {
      console.log("✅ Fetching Today Check-In/Out after 10s delay");

      const fetchTodayCheckInOut = async () => {
        try {
          const res = await fetch("https://api.hostaway.com/v1/listings", {
            headers: {
              Authorization: `Bearer ${API_TOKEN}`,
              "Content-Type": "application/json",
            },
          });
          const data = await res.json();
          let listings = data.result || [];

          // 🇴🇦 Exclude UAE listings
          listings = listings.filter(
            (listing) =>
              !(
                (listing.country &&
                  listing.country.toLowerCase().includes("united arab emirates")) ||
                (listing.countryCode &&
                  listing.countryCode.toUpperCase() === "AE") ||
                (listing.city && listing.city.toLowerCase().includes("dubai")) ||
                (listing.city && listing.city.toLowerCase().includes("abu dhabi"))
              )
          );

          const today = new Date();
          const todayStr = today.toISOString().split("T")[0];
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          const yesterdayStr = yesterday.toISOString().split("T")[0];

          const checkInList = [];
          const checkOutList = [];

          await Promise.all(
            listings.map(async (listing) => {
              const calUrl = `https://api.hostaway.com/v1/listings/${listing.id}/calendar?startDate=${yesterdayStr}&endDate=${todayStr}&includeResources=1`;
              const calRes = await fetch(calUrl, {
                headers: {
                  Authorization: `Bearer ${API_TOKEN}`,
                  "Content-Type": "application/json",
                },
              });
              if (!calRes.ok) return;
              const calData = await calRes.json();

              for (const dayData of calData.result || []) {
                for (const resv of dayData.reservations || []) {
                  try {
                    const resvUrl = `https://api.hostaway.com/v1/reservations/${resv.id}`;
                    const resvRes = await fetch(resvUrl, {
                      headers: {
                        Authorization: `Bearer ${API_TOKEN}`,
                        "Content-Type": "application/json",
                      },
                    });
                    if (!resvRes.ok) continue;
                    const resvData = await resvRes.json();
                    const reservation = resvData.result;

                    const vehicleNumber =
                      reservation?.customFieldValues?.find(
                        (field) => field.customField?.name === "Vehicle Number"
                      )?.value || "-";

                    // ✅ Get Actual Check-in Time custom field
                    const actualCheckinTime =
                      reservation?.customFieldValues?.find(
                        (field) => field.customField?.name === "Actual Check-in Time"
                      )?.value || null;

                    // ✅ Get Actual Check-out Time custom field
                    const actualCheckoutTime =
                      reservation?.customFieldValues?.find(
                        (field) => field.customField?.name === "Actual Check-out Time"
                      )?.value || null;

                    // ✅ Get Phone Number (safe fallbacks)
                    const phone =
                      reservation?.phone ||
                      "-";

                    const paymentStatus = reservation?.paymentStatus;

                    const guestObj = {
                      guest: reservation.guestName,
                      phone,
                      paymentStatus,
                      vehicle: vehicleNumber,
                      apartment:
                        listing.internalListingName || listing.name || "N/A",
                      arrival: reservation.arrivalDate,
                      departure: reservation.departureDate,
                      comment: reservation.comment || "-",
                      id: reservation.id,
                      actualCheckin: actualCheckinTime,
                      actualCheckout: actualCheckoutTime,
                    };

                    if (reservation.arrivalDate === todayStr)
                      checkInList.push(guestObj);
                    // ✅ Only show checkouts if they have actually checked in
                    if (reservation.departureDate === todayStr && actualCheckinTime)
                      checkOutList.push(guestObj);
                  } catch (innerErr) {
                    console.warn("⚠️ Failed fetching reservation details:", innerErr);
                  }
                }
              }
            })
          );

          // Filter double reservations on the same day for a room helper
          const filterDoubleReservations = (list) => {
            if (!list || list.length === 0) return [];
            
            const parseDate = (d) => {
              if (!d) return "";
              const clean = String(d).trim();
              if (clean.toLowerCase() === "n/a" || clean === "") return "";
              if (clean.includes("/")) {
                const parts = clean.split(",")[0].trim().split("/");
                if (parts.length === 3) {
                  const month = parts[0].padStart(2, "0");
                  const day = parts[1].padStart(2, "0");
                  const year = parts[2];
                  return `${year}-${month}-${day}`;
                }
              } else if (clean.includes("-")) {
                return clean.split("T")[0];
              }
              return clean;
            };

            const normRoom = (name) => {
              if (!name) return "";
              return String(name).replace(/\s+/g, "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            };

            const getProp = (r, propName) => {
              if (!r) return "";
              const f = r.fields || {};
              if (propName === "room") {
                return r.listingName || r.apartment || f["Listing Name"] || f["Apartment Name"] || f["Apartment Name "] || "";
              }
              if (propName === "arrival") {
                return r.arrivalDate || r.arrival || f["Arrival Date"] || f["Arrival Date "] || "";
              }
              if (propName === "departure") {
                return r.departureDate || r.departure || f["Departure Date"] || f["Departure Date "] || "";
              }
              if (propName === "checkin") {
                return r.actualCheckin || f["Actual Checkin"] || "";
              }
              if (propName === "checkout") {
                return r.actualCheckout || f["Actual Checkout"] || "";
              }
              if (propName === "id") {
                return r.reservationId || r.id || f["Reservation ID"] || f["Reservation ID "] || "";
              }
              return "";
            };

            // Group by reservation ID if available, otherwise fallback to room and stay dates
            const groups = {};
            list.forEach((r) => {
              const room = normRoom(getProp(r, "room"));
              const arrival = parseDate(getProp(r, "arrival"));
              const departure = parseDate(getProp(r, "departure"));
              const resId = getProp(r, "id");

              // Use reservation ID in the key to prevent merging different valid bookings
              const key = resId ? `res_${resId}` : `${room}_${arrival}_${departure}`;
              if (!groups[key]) groups[key] = [];
              groups[key].push(r);
            });

            const toRemove = new Set();

            Object.values(groups).forEach((group) => {
              if (group.length <= 1) return;

              // Check if there is one with check-in populated and checkout NOT populated
              const activeGuest = group.find((r) => {
                const checkinVal = getProp(r, "checkin");
                const checkoutVal = getProp(r, "checkout");
                const checkin = checkinVal && checkinVal !== "N/A" && String(checkinVal).trim() !== "";
                const checkout = checkoutVal && checkoutVal !== "N/A" && String(checkoutVal).trim() !== "";
                return checkin && !checkout;
              });

              if (activeGuest) {
                const activeId = getProp(activeGuest, "id");
                group.forEach((r) => {
                  if (getProp(r, "id") !== activeId) {
                    toRemove.add(getProp(r, "id"));
                  }
                });
                return;
              }

              // Otherwise, check if there is one with both check-in and checkout populated
              const completedGuest = group.find((r) => {
                const checkinVal = getProp(r, "checkin");
                const checkoutVal = getProp(r, "checkout");
                const checkin = checkinVal && checkinVal !== "N/A" && String(checkinVal).trim() !== "";
                const checkout = checkoutVal && checkoutVal !== "N/A" && String(checkoutVal).trim() !== "";
                return checkin && checkout;
              });

              if (completedGuest) {
                const completedId = getProp(completedGuest, "id");
                group.forEach((r) => {
                  if (getProp(r, "id") !== completedId) {
                    toRemove.add(getProp(r, "id"));
                  }
                });
              }
            });

            return list.filter((r) => !toRemove.has(getProp(r, "id")));
          };

          const filteredCheckInList = filterDoubleReservations(checkInList);
          const filteredCheckOutList = filterDoubleReservations(checkOutList);

          setTodayCheckIn(filteredCheckInList);
          setTodayCheckOut(filteredCheckOutList);

          // Fetch existing comments from Teable
          const allReservationIds = [...filteredCheckInList, ...filteredCheckOutList].map(r => r.id);
          fetchExistingComments(allReservationIds);
        } catch (err) {
          console.error("❌ Error fetching Check-In/Out:", err);
          setErrorCheck("Failed to load Check-In/Out data");
        } finally {
          setLoadingCheck(false);
        }
      };

      fetchTodayCheckInOut();
    }, 5000); // ⏱ Wait 10 seconds before fetching

    // Cleanup on unmount or tab switch
    return () => {
      clearTimeout(timer);
      window.__checkInOutCooldown = null; // reset cooldown if user left early
    };

  }, [activeTab]);

  const LISTINGS_DATA = {
    "2BR Premium": [305055, 309909, 323227, 288688],
    "3BR": [288686, 305327, 288676, 389366],
    "1BR": [307143, 306032, 288691, 305069, 288681, 288726, 288679, 288723, 288678, 323258, 400763, 387833, 387834],
    Studio: [288682, 288690, 323229, 323261, 336255, 383744, 410263, 413218, 392230, 449099],
    "2BR": [288677, 288684, 288687, 288977, 288685, 288683, 306543, 288724, 378076, 378078, 400779, 400769, 395345, 414090, 421015, 422302, 452492],
  };

  // Fetch data dynamically
  const fetchListings = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch("https://api.hostaway.com/v1/listings?country=Pakistan", {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) throw new Error("Failed to fetch listings");
      const data = await res.json();
      const listings = data.result || [];

      const today = new Date().toISOString().split("T")[0];

      // For each listing, fetch its calendar to get today’s status
      const results = await Promise.all(
        listings.map(async (listing) => {
          const calRes = await fetch(
            `https://api.hostaway.com/v1/listings/${listing.id}/calendar`,
            {
              headers: {
                Authorization: `Bearer ${API_TOKEN}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!calRes.ok) return null;
          const calData = await calRes.json();

          const todayEntry = calData.result?.find((d) => d.date === today);
          return todayEntry
            ? {
              id: listing.id,
              name: listing.internalListingName || `ID ${listing.id}`,
              status: todayEntry.status,
            }
            : null;
        })
      );

      const valid = results.filter(Boolean);

      // Group by your predefined categories
      const grouped = Object.fromEntries(
        Object.entries(LISTINGS_DATA).map(([category, ids]) => [
          category,
          valid.filter((v) => ids.includes(v.id)),
        ])
      );

      setListingSections(grouped);
    } catch (err) {
      console.error(err);
      setError("Failed to load apartment status");
    } finally {
      setLoading(false);
    }
  };

  // Fetch when Apartment Status tab opens
  useEffect(() => {
    if (activeTab !== 1) return;

    // 🕒 Prevent multiple rapid triggers (10s cooldown)
    if (window.__aptStatusCooldown && Date.now() - window.__aptStatusCooldown < 5000) {
      console.log("⏳ Skipping apartment fetch — still in cooldown");
      return;
    }

    // Mark cooldown start
    window.__aptStatusCooldown = Date.now();

    // 🟡 Show loading immediately
    setLoading(true);
    setError(null);
    setListingSections({});

    // Wait 10 seconds before fetching
    const timer = setTimeout(() => {
      console.log("🏢 Fetching Apartment Status after 10s delay");
      fetchListings();
    }, 5000);

    // Cleanup
    return () => {
      clearTimeout(timer);
      window.__aptStatusCooldown = null; // reset cooldown if user left early
    };

  }, [activeTab]);

  const handleLogout = () => {
    logout();
    navigate("/authentication/sign-in", { replace: true });
  };

  // Render content based on tab
  const renderContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Box sx={{ p: 1 }}>
            {children}
          </Box>
        ); // Home screen (current content)

      case 1:
        return (
          <Box sx={{ p: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: "700", color: "#1f2937" }}
              >
                🏢 Apartment Status
              </Typography>

              <TextField
                label="Search by Apartment Name"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                sx={{
                  width: "280px",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "60vh",
                }}
              >
                <Typography variant="h6" sx={{ color: "#6b7280", fontWeight: 500 }}>
                  Loading apartment data...
                </Typography>
              </Box>
            )}
            {error && <Typography color="error">{error}</Typography>}

            {!loading && !error && Object.keys(listingSections).length > 0 && (
              <Row>
                {Object.entries(listingSections).map(([category, entries], idx) => {
                  // Filter entries by search query
                  const filteredEntries = entries.filter((row) =>
                    row.name.toLowerCase().includes(searchQuery)
                  );

                  // Skip category if no matches
                  if (filteredEntries.length === 0) return null;

                  const total = entries.length;
                  const available = entries.filter((e) => e.status === "available").length;
                  const reserved = entries.filter((e) => e.status === "reserved").length;
                  const blocked = entries.filter((e) => e.status === "blocked").length;

                  return (
                    <Col key={idx} md={6} className="mb-4">
                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "12px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#f9fafb",
                            px: 3,
                            py: 2,
                            borderBottom: "1px solid #e5e7eb",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
                            {category}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500 }}>
                            Total: {total} |{" "}
                            <span style={{ color: "#10B981", fontWeight: 600 }}>Available: {available}</span>{" "}
                            |{" "}
                            <span style={{ color: "#EF4444", fontWeight: 600 }}>Reserved: {reserved}</span>{" "}
                            |{" "}
                            <span style={{ color: "#6B7280", fontWeight: 600 }}>Blocked: {blocked}</span>
                          </Typography>
                        </Box>

                        <Table striped bordered hover responsive style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>Listing Name</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredEntries.map((row, i) => (
                              <tr key={i}>
                                <td>{row.name}</td>
                                <td
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      row.status === "available"
                                        ? "#10B981"
                                        : row.status === "reserved"
                                          ? "#EF4444"
                                          : "#6B7280",
                                  }}
                                >
                                  {row.status}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Box>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 4 }}>
            {/* Header with title and Download button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h5" sx={{ mb: 3, fontWeight: "700", color: "#1f2937" }}>
                📅 Todays Check-In / Check-Out
              </Typography>

              {!loadingCheck && !(user?.role === "custom" && hasPermission("fdoPanel", "hideDownload")) && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* PDF Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={downloadPDF}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: "bold",
                      boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                      color: "#17621B",
                      border: "2px solid #17621B",
                      "&:hover": {
                        backgroundColor: "#1e7a20",
                        borderColor: "#145517",
                        color: "#fff",
                      },
                    }}
                  >
                    Download PDF
                  </Button>

                  {/* CSV Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={downloadExcel}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: "bold",
                      boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                      color: "#d97706",
                      border: "2px solid #d97706",
                      "&:hover": {
                        backgroundColor: "#f59e0b",
                        borderColor: "#d97706",
                        color: "#fff",
                      },
                    }}
                  >
                    Download Sheet
                  </Button>
                </Box>
              )}
            </Box>

            {loadingCheck && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "60vh",
                }}
              >
                <Typography variant="h6" sx={{ color: "#6b7280", fontWeight: 500 }}>
                  Loading data...
                </Typography>
              </Box>
            )}
            {errorCheck && <Typography color="error">{errorCheck}</Typography>}

            {!loadingCheck && !errorCheck && (
              <Row>
                {/* Check-In Table */}
                <Col md={6} className="mb-4">
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>

                    {/* Combined Header */}
                    <Box
                      sx={{
                        backgroundColor: "#f9fafb",
                        px: 3,
                        py: 2,
                        border: "2px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
                        Today Check-In &nbsp; <Typography component="span" variant="body2" sx={{ color: "#6b7280" }}>Total: {todayCheckIn.length}</Typography>
                      </Typography>

                    </Box>

                    <Table striped bordered hover responsive style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Guest Name</th>
                          <th>Vehicle Number</th>
                          <th>Apartment</th>
                          <th>Arrival Date</th>
                          <th>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayCheckIn.map((row, i) => (
                          <tr key={i}>
                            <td>{row.guest}</td>
                            <td>{row.vehicle}</td>
                            <td>{row.apartment}</td>
                            <td>{row.arrival}</td>
                            <td style={{ minWidth: "200px" }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <TextField
                                  size="small"
                                  placeholder="Add comment..."
                                  value={commentInputs[row.id] || ""}
                                  onChange={(e) => handleCommentChange(row.id, e.target.value)}
                                  fullWidth
                                  sx={{ backgroundColor: "#fff" }}
                                />
                                <IconButton
                                  onClick={() => handleSendComment(row)}
                                  color="success"
                                  size="small"
                                >
                                  <SendIcon />
                                </IconButton>
                              </Box>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Box>
                </Col>

                {/* Check-Out Table */}
                <Col md={6} className="mb-4">
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>

                    {/* Combined Header */}
                    <Box
                      sx={{
                        backgroundColor: "#f9fafb",
                        px: 3,
                        py: 2,
                        border: "2px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
                        Today Check-Out &nbsp; <Typography component="span" variant="body2" sx={{ color: "#6b7280" }}>Total: {todayCheckOut.length}</Typography>
                      </Typography>

                    </Box>

                    <Table striped bordered hover responsive style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Guest Name</th>
                          <th>Vehicle Number</th>
                          <th>Apartment</th>
                          <th>Departure Date</th>
                          <th>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayCheckOut.map((row, i) => (
                          <tr key={i}>
                            <td>{row.guest}</td>
                            <td>{row.vehicle}</td>
                            <td>{row.apartment}</td>
                            <td>{row.departure}</td>
                            <td style={{ minWidth: "200px" }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <TextField
                                  size="small"
                                  placeholder="Add comment..."
                                  value={commentInputs[row.id] || ""}
                                  onChange={(e) => handleCommentChange(row.id, e.target.value)}
                                  fullWidth
                                  sx={{ backgroundColor: "#fff" }}
                                />
                                <IconButton
                                  onClick={() => handleSendComment(row)}
                                  color="success"
                                  size="small"
                                >
                                  <SendIcon />
                                </IconButton>
                              </Box>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Box>
                </Col>
              </Row>
            )}
          </Box>
        );

      case 3:
        return <Upsells defaultTab={0} isEmbedded={true} />;

      case 4:
        return <Upsells defaultTab={1} isEmbedded={true} />;

      case 5:
        return <Upsells defaultTab={2} isEmbedded={true} />;

      case 6:
        return <QBPayment />;

      default:
        return children;
    }
  };

  console.log("🧭 Active Tab:", activeTab);

  return (
    <MDBox sx={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      {/* Top Navigation Bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#ffffff",
          color: "#1f2937",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 0.5, sm: 2 }, py: 1 }}>
          {/* Left: Logo & Title */}
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1 }} sx={{ flexShrink: 0, mr: { xs: 0.5, sm: 2 } }}>
            <Logo
              width={{ xs: "50px", sm: "80px" }}
              height={{ xs: "50px", sm: "80px" }}
            />
            <Box display="flex" flexDirection="column">
              <Typography variant={{ xs: "subtitle1", sm: "h5" }} sx={{ fontWeight: "700", color: "#1f2937", fontSize: { xs: "0.9rem", sm: "1.5rem" } }} noWrap>
                FDO Panel
              </Typography>
              <Typography
                variant={{ xs: "caption", sm: "body2" }}
                sx={{ color: "#6b7280", fontSize: { xs: "0.55rem", sm: "0.75rem" }, fontWeight: "500" }}
                noWrap
              >
                Guest Management System
              </Typography>
            </Box>
          </Box>

          {/* Center Tabs: Hidden on mobile, shown on desktop */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", overflow: "hidden" }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.85rem",
                    mx: 0.5,
                    px: 2,
                    py: 1,
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    color: "#4b5563",
                    backgroundColor: "transparent",
                    minWidth: "90px",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                  "& .Mui-selected": {
                    color: "#000 !important",
                    backgroundColor: "transparent !important",
                    border: "1.5px solid #000",
                    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Tab label="Home" />
                <Tab label="Apartment Status" />
                <Tab label="Todays Check-In/Out" />
                <Tab label="Add Upsells / Charges" />
                <Tab label="Upsell List" />
                <Tab label="Charges List" />
                <Tab label="QB Payments" />
              </Tabs>
            </Box>
          )}

          {/* Right: User Info & Logout */}
          <Box display="flex" alignItems="center" gap={2}>
            {user && (
              <Box display="flex" alignItems="center" gap={{ xs: 0, sm: 1 }} sx={{ flexShrink: 0, ml: { xs: 0.5, sm: 2 } }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                  }}
                >
                  {(user?.name || user?.username)?.charAt(0)?.toUpperCase() || "U"}
                </Avatar>
                <Typography sx={{ color: "#1f2937", fontWeight: 600, display: { xs: "none", sm: "block" } }}>
                  {user?.name || user?.username}
                </Typography>

                {/* NOTIFICATION ICON */}
                <IconButton
                  ref={anchorRef}
                  size="small"
                  sx={{
                    color: "#4b5563",
                    "&:hover": { backgroundColor: "#f3f4f6" },
                  }}
                  onClick={() => setNotificationsOpen(true)}
                >
                  <Badge
                    badgeContent={
                      unreadCount > 20
                        ? "20+"
                        : unreadCount
                    }
                    color="error"
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Box>
            )}

            <MDButton
              variant="outlined"
              onClick={handleLogout}
              sx={{
                borderColor: "#ef4444",
                color: "#ef4444",
                borderRadius: "10px",
                px: { xs: 1.5, sm: 3 },
                py: { xs: 0.5, sm: 1 },
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                minWidth: { xs: "auto", sm: "auto" },
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: "#ef4444",
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, ml: 0.5 }}>
                Logout
              </Box>
            </MDButton>
          </Box>
        </Toolbar>

        {/* Mobile Tabs Row */}
        {isMobile && (
          <Box sx={{ borderTop: "1px solid #f1f5f9", backgroundColor: "#ffffff", py: 0.5 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.65rem",
                  py: 0.5,
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  color: "#4b5563",
                  backgroundColor: "transparent",
                  minWidth: "60px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  "& .MuiTab-iconWrapper": {
                    marginBottom: "1px",
                    marginRight: 0,
                  },
                },
                "& .Mui-selected": {
                  color: "#000 !important",
                  backgroundColor: "transparent !important",
                  border: "1.5px solid #000",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                },
              }}
            >
              <Tab
                icon={<HomeIcon sx={{ fontSize: "20px" }} />}
                label="Home"
              />
              <Tab
                icon={<ApartmentIcon sx={{ fontSize: "20px" }} />}
                label="Status"
              />
              <Tab
                icon={<EventAvailableIcon sx={{ fontSize: "20px" }} />}
                label="Today"
              />
              <Tab
                icon={<PersonSearchIcon sx={{ fontSize: "20px" }} />}
                label="Hostaway"
              />
              <Tab
                icon={<LocalOfferIcon sx={{ fontSize: "20px" }} />}
                label="Upsells"
              />
              <Tab
                icon={<ReceiptLongIcon sx={{ fontSize: "20px" }} />}
                label="Charges"
              />
              <Tab
                icon={<AccountBalanceWalletIcon sx={{ fontSize: "20px" }} />}
                label="QB Pay"
              />
            </Tabs>
          </Box>
        )}
      </AppBar>

      {/* Main Content */}
      <MDBox>{renderContent()}</MDBox>
      <Notifications
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        anchorEl={anchorRef.current}
        onMarkAsRead={() => setUnreadCount(0)}
        onNotificationClick={(reservationId) => {
          setNotificationsOpen(false);
          setActiveTab(0);
          // Navigate with query param to trigger opening the modal in FDOPanel
          // Use replace: true if you don't want to clutter history, or push (default)
          navigate(`/fdo-panel?reservationId=${reservationId}`);
        }}
      />

      {/* ✅ Global Notification Stack */}
      <Stack
        spacing={1}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 9999,
          maxWidth: 400,
        }}
      >
        {snackPack.map((snack) => (
          <Grow key={snack.id} in={true}>
            <Alert
              onClick={() => {
                if (snack.reservationId) {
                  navigate(`/fdo-panel?reservationId=${snack.reservationId}`);
                  setSnackPack((prev) => prev.filter((item) => item.id !== snack.id));
                }
              }}
              style={{ cursor: snack.reservationId ? "pointer" : "default" }}
              onClose={() => setSnackPack((prev) => prev.filter((item) => item.id !== snack.id))}
              severity={snack.severity || "info"}
              variant="filled"
              sx={{
                width: "100%",
                minWidth: 320,
                borderRadius: "12px",
                fontWeight: "bold",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                background: snack.richContent
                  ? "linear-gradient(135deg, #3b82f6, #1d4ed8)"
                  : (() => {
                    switch (snack.severity) {
                      case "error": return "#F8D7DA";
                      case "warning": return "#FFF3CD";
                      case "info": return "#CCE5FF";
                      case "success": return "#D4EDDA";
                      default: return undefined;
                    }
                  })(),
                color: snack.richContent
                  ? "#fff"
                  : (() => {
                    switch (snack.severity) {
                      case "error": return "#842029";
                      case "warning": return "#856404";
                      case "info": return "#084298";
                      case "success": return "#155724";
                      default: return undefined;
                    }
                  })(),
              }}
            >
              {snack.richContent || snack.message}
            </Alert>
          </Grow>
        ))}
      </Stack>
    </MDBox>
  );
}

UserLayout.propTypes = {
  children: PropTypes.node,
};

export default UserLayout;
